import { useState, useEffect, useCallback } from 'react';
import { Attendee } from '../../types/event/attendee';
import { attendeeService } from '../../services/event/attendeeService';
import { format } from 'date-fns';

export const useAttendees = (eventUuid: string) => {
    const [attendees, setAttendees] = useState<Attendee[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    const fetchAttendees = useCallback(async (): Promise<void> => {
        try {
            setLoading(true);
            setError(null);
            const data = await attendeeService.getEventAttendees(eventUuid);
            setAttendees(data);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred');
        } finally {
            setLoading(false);
        }
    }, [eventUuid]);

    const refreshAttendees = useCallback(async (): Promise<void> => {
        await fetchAttendees();
    }, [fetchAttendees]);

    useEffect(() => {
        // Start fetching data
        fetchAttendees();
        
        // Add a 1-second buffer to the loading animation for initial load only
        const timer = setTimeout(() => {
            setLoading(false);
        }, 1000);
        
        // Clean up the timer if component unmounts
        return () => clearTimeout(timer);
    }, [eventUuid]);

    const handleExportCSV = () => {
        // Create CSV header
        const headers = ['Name', 'Email', 'Phone', 'Ticket Type', 'Status', 'Purchase Date'];
        
        // Create CSV rows
        const rows = attendees.map(attendee => [
            attendee.name,
            attendee.email,
            attendee.phone || '-',
            attendee.ticket_type,
            attendee.ticket_burned ? 'Used' : 'Unused',
            format(new Date(attendee.purchase_date), 'MMM d, yyyy')
        ]);
        
        // Combine headers and rows
        const csvContent = [
            headers.join(','),
            ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
        ].join('\n');
        
        // Create blob and download
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `attendees_${format(new Date(), 'yyyy-MM-dd')}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return {
        attendees,
        loading,
        error,
        handleExportCSV,
        refreshAttendees
    };
}; 